import { apiClient } from './client';
/**
 * Thrown by {@link sendMessage} when the server returns 409 because another
 * user is mid-turn on a shared asset-chat session. Carries the holder identity
 * so the caller can render "Joel is editing…" without an extra lookup.
 */
export class TurnAlreadyActiveError extends Error {
    constructor(body) {
        super(body.message || 'Another user is asking Webster.');
        Object.defineProperty(this, "sessionId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "turnUserId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "turnUserName", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "turnStartedAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = 'TurnAlreadyActiveError';
        this.sessionId = body.sessionId ?? null;
        this.turnUserId = body.turnUserId ?? null;
        this.turnUserName = body.turnUserName ?? null;
        this.turnStartedAt = body.turnStartedAt ?? null;
    }
}
/**
 * Thrown by {@link sendMessage} when the server returns 410 because the
 * conversation session has expired (ExpiresAtUtc passed). The caller should
 * start a fresh session rather than retry.
 */
export class ConversationExpiredError extends Error {
    constructor(body) {
        super(body.message || 'This conversation has expired. Start a new one.');
        Object.defineProperty(this, "sessionId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "expiredAtUtc", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.name = 'ConversationExpiredError';
        this.sessionId = body.sessionId ?? null;
        this.expiredAtUtc = body.expiredAtUtc ?? null;
    }
}
/**
 * The middleware wraps error bodies as `{ error: "<json string>", stackTrace: "" }`,
 * so the structured fields land on `result.error` as a JSON string. Try to parse
 * it and return the inner object; fall through to a plain shape if parsing fails.
 */
function parseStructuredError(raw) {
    if (typeof raw !== 'string')
        return {};
    try {
        const parsed = JSON.parse(raw);
        return typeof parsed === 'object' && parsed !== null ? parsed : {};
    }
    catch {
        return {};
    }
}
/**
 * Start a new AI conversation
 */
export async function startConversation(clientCompanyId, workspaceId, context) {
    const request = {
        Context: context,
    };
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations`, {
        method: 'POST',
        body: request,
        requiresAuth: true,
    });
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to start conversation');
    }
    return response.data;
}
/**
 * Fetch an existing AI conversation session (and its messages) by id.
 * Used to restore Webster chat history on page reload.
 */
export async function getConversation(clientCompanyId, workspaceId, sessionId) {
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}`, {
        method: 'GET',
        requiresAuth: true,
    });
    if (response.error === 'Resource not found')
        return null;
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to load conversation');
    }
    return response.data;
}
/**
 * Send a message in an existing conversation
 */
export async function sendMessage(clientCompanyId, workspaceId, sessionId, message, updatedContext) {
    const request = {
        Message: message,
        UpdatedContext: updatedContext,
    };
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Messages`, {
        method: 'POST',
        body: request,
        requiresAuth: true,
    });
    // Surface multi-user turn-lock + session-expiration as typed errors so the
    // caller can render holder-aware messages ("Joel is editing…") instead of a
    // generic toast. Race window is small in practice but the typed errors keep
    // the failure path coherent when it does fire.
    if (response.status === 409) {
        const body = parseStructuredError(response.error);
        if (body.error === 'TurnAlreadyActive') {
            throw new TurnAlreadyActiveError({
                sessionId: body.sessionId,
                turnUserId: body.turnUserId,
                turnUserName: body.turnUserName,
                turnStartedAt: body.turnStartedAt,
                message: body.message,
            });
        }
    }
    if (response.status === 410) {
        const body = parseStructuredError(response.error);
        if (body.error === 'ConversationExpired') {
            throw new ConversationExpiredError({
                sessionId: body.sessionId,
                expiredAtUtc: body.expiredAtUtc,
                message: body.message,
            });
        }
    }
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to send message');
    }
    return response.data;
}
/**
 * Apply AI-generated content to a field
 */
export async function applyToField(clientCompanyId, workspaceId, sessionId, fieldName, value, variationIndex, messageIndex) {
    const request = {
        MessageIndex: messageIndex ?? 0,
        FieldName: fieldName,
        Value: value,
        VariationIndex: variationIndex,
    };
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Apply`, {
        method: 'POST',
        body: request,
        requiresAuth: true,
    });
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to apply value');
    }
    return response.data;
}
/**
 * Complete/end a conversation
 */
export async function completeConversation(clientCompanyId, workspaceId, sessionId, action, selectedContent, selectedVariationIndex, rating) {
    const request = {
        Action: action,
        SelectedContent: selectedContent,
        SelectedVariationIndex: selectedVariationIndex,
        Rating: rating,
    };
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Complete`, {
        method: 'POST',
        body: request,
        requiresAuth: true,
    });
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to complete conversation');
    }
    return response.data;
}
/**
 * Update context in an ongoing conversation
 * (sends a message with updated context)
 */
export async function updateContext(clientCompanyId, workspaceId, sessionId, context) {
    await sendMessage(clientCompanyId, workspaceId, sessionId, '[Context Updated]', context);
}
/**
 * Update feedback for a completed conversation
 */
export async function updateFeedback(clientCompanyId, workspaceId, sessionId, rating, feedbackComment) {
    const request = {
        Rating: rating,
        FeedbackComment: feedbackComment,
    };
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Feedback`, {
        method: 'POST',
        body: request,
        requiresAuth: true,
    });
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to update feedback');
    }
    return response.data;
}
/**
 * Update feedback for a specific message in a conversation
 */
export async function updateMessageFeedback(clientCompanyId, workspaceId, sessionId, messageIndex, rating, feedbackComment) {
    const request = {
        Rating: rating,
        FeedbackComment: feedbackComment,
    };
    const response = await apiClient.request(`/api/ClientCompanies/${clientCompanyId}/Workspaces/${workspaceId}/AI/Conversations/${sessionId}/Messages/${messageIndex}/Feedback`, {
        method: 'PUT',
        body: request,
        requiresAuth: true,
    });
    if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to update message feedback');
    }
    return response.data;
}
